import { Link } from "react-router-dom";
import type { Book } from "@/data/books";
import {ArrowRight, CheckCircle, Clock} from 'lucide-react';

interface BookCardProps {
  book: Book;
  index?: number;
}

const statusConfig = {
  complete: {
    label: "Complete",
    className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/40",
    icon: CheckCircle,
  },
  "in-progress": {
    label: "In Progress",
    className: "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 border-amber-200 dark:border-amber-800/40",
    icon: Clock,
  },
  forthcoming: {
    label: "Coming Soon",
    className: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800/50 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700/40",
    icon: Clock,
  },
};

export default function BookCard({ book, index = 0 }: BookCardProps) {
  const status = statusConfig[book.status] || statusConfig["forthcoming"];
  const StatusIcon = status.icon;

  return (
    <Link
      to={`/book/${book.id}`}
      className="group block opacity-0 animate-fade-in-up"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="relative overflow-hidden rounded-2xl bg-zinc-100 dark:bg-zinc-900 transition-all duration-500 hover:shadow-2xl hover:shadow-[#C5A572]/10">
        {/* Cover Image */}
        <div className="relative aspect-[2/3] overflow-hidden">
          <img
            src={book.coverImage}
            alt={book.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Category Badge */}
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full bg-white/90 dark:bg-zinc-900/90 backdrop-blur-sm text-zinc-700 dark:text-zinc-300">
              {book.category === "fiction" ? "Fiction" : "Non-Fiction"}
            </span>
          </div>

          {/* Hover CTA */}
          <div className="absolute bottom-4 left-4 right-4 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-white">
              View Details
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="p-5">
          <h3 className="font-serif text-lg font-semibold text-zinc-900 dark:text-white mb-1 group-hover:text-[#C5A572] transition-colors duration-300">
            {book.title}
          </h3>
          {book.series && (
            <p className="text-xs text-[#C5A572] font-medium mb-2">{book.series}</p>
          )}
          <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed line-clamp-2 mb-3">
            {book.synopsis}
          </p>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] font-semibold rounded-full border ${status.className}`}>
              <StatusIcon size={10} />
              {status.label}
            </span>
            <span className="text-xs text-zinc-400 dark:text-zinc-500">{book.genre}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
